<script setup lang="ts">
import {
  useFloating,
  autoUpdate,
  autoPlacement,
  offset,
  shift,
  type Strategy,
  type AutoPlacementOptions,
} from '@floating-ui/vue';
import { onClickOutside, useEventListener } from '@vueuse/core';
import {
  type ClassValue,
  type ComponentPublicInstance,
  type TeleportProps,
  toRef,
  useSlots,
  useTemplateRef,
} from 'vue';

const opened = defineModel<boolean>('opened', { default: false });
const reference = defineModel<HTMLElement | ComponentPublicInstance | null>('reference');

const {
  teleport,
  contentClass,
  strategy,
  closeOnClickOutside = false,
  closeOnScroll = false,
  placement,
} = defineProps<{
  teleport?: TeleportProps['to'];
  contentClass?: ClassValue;
  strategy?: Strategy;
  closeOnClickOutside?: boolean;
  closeOnScroll?: boolean;
  placement?: AutoPlacementOptions;
}>();

const slots = useSlots();

const referenceRef = useTemplateRef<HTMLElement>('referenceRef');
const contentRef = useTemplateRef<HTMLElement>('contentRef');

const { floatingStyles } = useFloating(
  toRef(() => (reference.value ? reference.value : referenceRef.value)),
  contentRef,
  {
    open: opened,
    middleware: [offset(8), autoPlacement(placement), shift({ padding: 8 })],
    strategy: () => strategy,
    whileElementsMounted: autoUpdate,
  },
);

const close = () => {
  if (opened.value) {
    opened.value = false;
  }
};

useEventListener(
  window,
  'scroll',
  () => {
    if (closeOnScroll) {
      close();
    }
  },
  { capture: true },
);

onClickOutside(contentRef, () => {
  if (closeOnClickOutside) {
    close();
  }
});
</script>

<template>
  <div
    v-if="slots.reference"
    ref="referenceRef"
  >
    <slot name="reference" />
  </div>

  <Teleport
    :to="teleport"
    :disabled="!teleport"
  >
    <Transition
      enter-active-class="transition-[opacity] duration-300 ease-out motion-reduce:duration-0"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-[opacity] duration-200 ease-in motion-reduce:duration-0"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-show="opened"
        ref="contentRef"
        :style="floatingStyles"
        :class="contentClass"
        class="tooltip z-1000 rounded-2xl p-1 text-[var(--theme-text-primary)] shadow-sm"
      >
        <slot name="content" />
      </div>
    </Transition>
  </Teleport>
</template>

<style>
.tooltip {
  background-color: var(--theme-surface);
}
</style>
